import React, { useEffect, useState } from "react";
import logo_full from "../assets/logo-full.svg";
import styled, { css, useTheme } from "styled-components";
import { Button } from "./Button";
import { Link } from "gatsby";
import { MdClose, MdMenu } from "react-icons/md";
import { useColors } from "../hooks/colors";
import { useLocation } from "@gatsbyjs/reach-router";

type NavigationLink = {
  name: string;
  path: string;
};

const links: NavigationLink[] = [
  { name: "Our Work", path: "/our-work" },
  { name: "Company", path: "/company" },
  { name: "Careers", path: "/careers" },
  { name: "Blog", path: "/blog" },
];

export const MAX_WIDTH_MOBILE_NAV = 920;

export const Nav = styled.header<{ isFilled: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 0 50px;
  height: 72px;
  align-items: center;
  width: 100%;
  transition: 0.3s;
  transition-timing-function: ease-in-out;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 99;

  @media (max-width: ${MAX_WIDTH_MOBILE_NAV}px) {
    padding: 0 32px;
  }

  ${({ isFilled }) =>
    isFilled
      ? css`
          background: white;
          box-shadow: 0 2px 1px -1px hsl(0deg 0% 96%);
        `
      : ""}
`;

const SCROLL_THRESHOLD = 50;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  font-weight: bold;
  @media (max-width: ${MAX_WIDTH_MOBILE_NAV}px) {
    display: none;
  }

  a:not(:last-child) {
    margin-right: 19px;
  }
`;

const MobileNav = styled.nav`
  flex-direction: column;
  align-items: flex-start;
  left: 0;
  position: fixed;
  right: 0;
  top: 4rem;
  z-index: 10000;
  bottom: 0;
  display: flex;
  height: 100%;
  width: 100%;
  background: white;
  padding: 1.5rem 2rem;
`;

const MobileNavToggle = styled.button`
  display: flex;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 30px;

  @media (min-width: ${MAX_WIDTH_MOBILE_NAV + 1}px) {
    display: none;
  }
`;

const NavTitle = styled((props) => <Link {...props} />)`
  display: flex;
  color: ${(props) => props.theme.confusion}!important;
  font-weight: bold;
  font-size: 24px;
  align-items: center;
  line-height: 1;
  img {
    height: 32px;
    margin-right: 10px;
  }
`;

const NavItem = styled.div<{
  accent: string;
  isActive: boolean;
}>`
  font-size: 16px;
  color: ${(props) => props.theme.confusion};
  border-block: 3px solid transparent;
  border-bottom-color: ${({ accent, isActive }) =>
    isActive ? accent : "transparent"};
  cursor: pointer;

  &:hover {
    border-bottom-color: ${({ accent }) => accent};
  }

  @media (max-width: ${MAX_WIDTH_MOBILE_NAV}px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const NavButton = styled(Button)`
  padding: 7px 14px !important;
  font-size: 1em;
`;

export const Navigation = () => {
  const { peace } = useTheme();
  const accents = useColors();

  const [scrollTop, setScrollTop] = useState(0);
  const [isMobileNavVisible, setMobileNavVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrollTop(document.body.scrollTop);
    };

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const location = useLocation();

  const isScrollingPastThreshold = scrollTop >= SCROLL_THRESHOLD;

  const navItems = links.map((link, index) => (
    <Link key={index} to={link.path}>
      <NavItem
        accent={accents[index % accents.length]}
        // all link paths are written without a trailing slash, so we need to
        // remove it from the current path to match
        isActive={location.pathname.replace(/\/$/, "") === link.path}
      >
        {link.name}
      </NavItem>
    </Link>
  ));

  navItems.push(
    <NavButton
      key="mailing-list"
      color={peace}
      as="a"
      href="http://eepurl.com/iawHLb"
      target="_blank"
      rel="noopener noreferrer"
    >
      Get updates
    </NavButton>
  );

  return (
    <Nav isFilled={isScrollingPastThreshold}>
      <NavTitle to="/">
        <img src={logo_full} alt="Imbue logo" />
      </NavTitle>
      <MobileNavToggle
        aria-label="toggle mobile navigation"
        onClick={() => setMobileNavVisible(!isMobileNavVisible)}
      >
        {isMobileNavVisible ? <MdClose /> : <MdMenu />}
      </MobileNavToggle>
      <DesktopNav>{navItems}</DesktopNav>
      {isMobileNavVisible && (
        <MobileNav onClick={() => setMobileNavVisible(false)}>
          {navItems}
        </MobileNav>
      )}
    </Nav>
  );
};
